/**
 * Smart Notification Wrapper
 * Intelligently handles notifications based on user preferences
 * Supports toast, browser notifications, sounds, and notification center
 */

import toast from 'react-hot-toast';
import {
  showBrowserNotification,
  playNotificationSound,
  shouldShowBrowserNotification,
} from './browserNotifications';
import { NotificationEventType } from '../types/notificationPreferences';
import React from 'react';
import { Fingerprint, MapPin, Home, Wifi } from 'lucide-react';
import { LicensePlate } from '../components/ui/LicensePlate';

/**
 * Dispatch custom event to add notification to the notification center
 */
const addToNotificationCenter = (
  title: string,
  message: string,
  type: 'info' | 'success' | 'warning' | 'error',
  metadata?: Record<string, any>
) => {
  const notification = {
    id: `${Date.now()}-${Math.random()}`,
    title,
    message,
    type,
    timestamp: new Date(),
    read: false,
    metadata
  };
  console.log(' [SmartNotification] Adding to notification center:', notification);
  window.dispatchEvent(new CustomEvent('addNotification', {
    detail: notification
  }));
};

/**
 * Custom toast component for access events — matches the sidebar EventCard design exactly.
 *
 * Vehicle events: LicensePlate(md) hero on the left, person name + unit + direction on right.
 * Normal events:  full-height photo strip on the left, person info on the right.
 */
const AccessEventToast = ({
  title,
  message,
  metadata,
  type,
  toastId,
  clickable = true,
}: {
  title: string;
  message: string;
  metadata?: Record<string, any>;
  type: 'success' | 'error' | 'warning' | 'info';
  toastId?: string;
  clickable?: boolean;
}) => {
  const isLight = typeof document !== 'undefined' && document.body.classList.contains('panther-light-toast');

  // Direction badge
  const dir = (metadata?.direction || '').toLowerCase();
  let dirLabel = 'Acesso';
  let dirBg = isLight ? 'bg-gray-200' : 'bg-gray-500/20';
  let dirText = isLight ? 'text-gray-600' : 'text-gray-400';
  if (metadata?.isRemoteTrigger) {
    dirLabel = 'Remota'; dirBg = isLight ? 'bg-indigo-100' : 'bg-indigo-500/20'; dirText = isLight ? 'text-indigo-700' : 'text-indigo-400';
  } else if (metadata?.isManual) {
    if (dir === 'entrada' || dir === 'entry') {
      dirLabel = 'Entrada Manual'; dirBg = isLight ? 'bg-amber-100' : 'bg-amber-500/20'; dirText = isLight ? 'text-amber-700' : 'text-amber-400';
    } else {
      dirLabel = 'Saída Manual'; dirBg = isLight ? 'bg-orange-100' : 'bg-orange-500/20'; dirText = isLight ? 'text-orange-700' : 'text-orange-400';
    }
  } else if (dir === 'entrada' || dir === 'entry') {
    dirLabel = 'Entrada'; dirBg = isLight ? 'bg-green-100' : 'bg-green-500/20'; dirText = isLight ? 'text-green-700' : 'text-green-400';
  } else if (dir === 'saida' || dir === 'saída' || dir === 'exit') {
    dirLabel = 'Saída'; dirBg = isLight ? 'bg-blue-100' : 'bg-blue-500/20'; dirText = isLight ? 'text-blue-700' : 'text-blue-400';
  } else if (dir === 'passagem' || dir === 'passage') {
    dirLabel = 'Passagem'; dirBg = isLight ? 'bg-blue-100' : 'bg-blue-500/20'; dirText = isLight ? 'text-blue-700' : 'text-blue-400';
  }

  const borderColor = metadata?.isRemoteTrigger ? 'border-l-indigo-500' : type === 'success' ? 'border-l-green-500' : type === 'warning' ? 'border-l-orange-500' : type === 'info' ? 'border-l-blue-500' : 'border-l-red-500';
  const isVehicle = !!metadata?.vehiclePlate;
  const isUnknownTag = !isVehicle && /^\d{5,}$/.test((metadata?.personName || '').trim());
  // RFID events without vehicle plate: when we have the owner's photo, show it with a small
  // tag badge overlay; otherwise fall back to a full Wifi icon block.
  const isRFIDPersonAccess = !isVehicle && !isUnknownTag && metadata?.accessType === 'rfid';
  const STREET_PREFIXES = /^(Rua|R\.|Av\.|Avenida|Av |Alameda|Al\.|Travessa|Tv\.|Estrada|Est\.|Rodovia|Rod\.|Via |Viela|Largo|Praça|Pça\.|Condomínio|Cond\.)\s/i;
  const parseUnitStreet = (u: string | undefined | null): { unit: string | null; street: string | null } => {
    if (!u) return { unit: null, street: null };
    const parts = u.split(' - ');
    if (parts.length >= 2) {
      const last = parts[parts.length - 1];
      if (STREET_PREFIXES.test(last) || last.includes(',')) {
        return { unit: parts.slice(0, -1).join(' - '), street: last };
      }
    }
    return { unit: u, street: null };
  };
  const _parsed = metadata?.streetName
    ? { unit: metadata?.unitName || null, street: metadata.streetName }
    : parseUnitStreet(metadata?.unitName);
  const locationInfo = metadata?.departmentName || _parsed.unit || null;
  const streetInfo = metadata?.streetName || _parsed.street || null;

  const handlePersonClick = () => {
    if (!clickable) return;
    window.dispatchEvent(new CustomEvent('openUserModal', {
      detail: {
        personName: metadata?.personName, personId: metadata?.personId,
        facePhotoUrl: metadata?.facePhotoUrl, customerName: metadata?.customerName,
        unitName: metadata?.unitName, departmentName: metadata?.departmentName,
        personRole: metadata?.personRole,
        vehicleId: metadata?.vehicleId,
        vehiclePlate: metadata?.vehiclePlate,
      }
    }));
  };

  const handleClose = (e: any) => { e.stopPropagation(); if (toastId) toast.dismiss(toastId); };

  const closeSvg = React.createElement('button', {
    key: 'close', onClick: handleClose,
    className: 'absolute top-1.5 right-1.5 p-1 hover:bg-white/10 rounded-full transition-colors',
    'aria-label': 'Fechar',
  }, React.createElement('svg', {
    className: 'w-3 h-3 text-gray-500', fill: 'none', strokeLinecap: 'round', strokeLinejoin: 'round',
    strokeWidth: '2.5', viewBox: '0 0 24 24', stroke: 'currentColor',
  }, React.createElement('path', { d: 'M6 18L18 6M6 6l12 12' })));

  const dirBadge = React.createElement('span', {
    key: 'dir', className: `inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${dirBg} ${dirText}`,
  }, dirLabel);

  const zoneEl = (metadata?.zoneName || metadata?.deviceNameFallback) ? React.createElement('span', {
    key: 'zone', className: isLight ? 'flex items-center gap-0.5 text-[10px] text-gray-600' : 'flex items-center gap-0.5 text-[10px] text-gray-300',
  }, [
    React.createElement(MapPin, { key: 'pin', className: 'h-2.5 w-2.5 text-gray-400 flex-shrink-0' }),
    React.createElement('span', { key: 'zn', className: 'truncate max-w-[140px]' }, metadata.zoneName || metadata.deviceNameFallback),
  ]) : null;

  const metaRow = (metadata?.personRole || locationInfo || streetInfo) ? React.createElement('div', {
    key: 'meta', className: 'flex flex-col gap-0.5 mt-0.5',
  }, [
    (metadata?.personRole || locationInfo) && React.createElement('div', {
      key: 'role-loc', className: 'flex items-center gap-1 flex-wrap',
    }, [
      metadata?.personRole && React.createElement('span', {
        key: 'role',
        className: isLight ? 'px-1.5 py-0.5 text-[9px] font-medium bg-gray-200 text-gray-700 rounded' : 'px-1.5 py-0.5 text-[9px] font-medium bg-gray-700 text-gray-300 rounded',
      }, metadata.personRole),
      locationInfo && React.createElement('span', {
        key: 'loc', className: isLight ? 'flex items-center gap-0.5 text-[10px] text-gray-500' : 'flex items-center gap-0.5 text-[10px] text-gray-400',
      }, [
        React.createElement(Home, { key: 'hi', className: 'h-2.5 w-2.5 flex-shrink-0' }),
        React.createElement('span', { key: 'ln', className: 'truncate max-w-[120px]' }, locationInfo),
      ]),
    ]),
    streetInfo && React.createElement('span', {
      key: 'street', className: isLight ? 'flex items-center gap-0.5 text-[10px] text-gray-500' : 'flex items-center gap-0.5 text-[10px] text-gray-400',
    }, [
      React.createElement(MapPin, { key: 'mp', className: 'h-2.5 w-2.5 flex-shrink-0' }),
      React.createElement('span', { key: 'sn', className: 'truncate max-w-[160px]' }, streetInfo),
    ]),
  ]) : null;

  // ══ VEHICLE LAYOUT — LicensePlate(md) hero on left, matches sidebar ══════
  if (isVehicle) {
    return React.createElement('div', {
      className: isLight
        ? `relative flex items-start gap-2.5 p-2 overflow-hidden rounded-lg border border-gray-300 border-l-4 ${borderColor} bg-white shadow-lg ${clickable ? 'cursor-pointer hover:bg-gray-50' : 'cursor-default'}`
        : `relative flex items-start gap-2.5 p-2 overflow-hidden rounded-lg border border-gray-800 border-l-4 ${borderColor} bg-gray-950 shadow-2xl ${clickable ? 'cursor-pointer hover:bg-gray-900/80' : 'cursor-default'}`,
      style: { width: '340px', paddingRight: '28px' },
      onClick: clickable ? handlePersonClick : undefined,
    }, [
      React.createElement(LicensePlate, { key: 'plate', plate: metadata?.vehiclePlate || '', size: 'md', maxLength: 8, className: 'flex-shrink-0 mt-0.5' }),
      React.createElement('div', { key: 'right', className: 'flex-1 min-w-0' }, [
        metadata?.personName && React.createElement('p', {
          key: 'pname',
          className: isLight ? 'text-[11px] font-medium text-gray-800 leading-tight truncate' : 'text-[11px] font-medium text-gray-200 leading-tight truncate',
        }, metadata.personName),
        metaRow,
        React.createElement('div', { key: 'dir-row', className: 'flex items-center gap-1.5 mt-1 flex-wrap' }, [dirBadge, zoneEl]),
      ]),
      closeSvg,
    ]);
  }

  // ══ NORMAL / UNKNOWN TAG LAYOUT — photo strip on left ════════════════════
  return React.createElement('div', {
    className: isLight
      ? `relative flex overflow-hidden rounded-lg border border-gray-300 border-l-4 ${borderColor} bg-white shadow-lg transition-colors ${clickable ? 'cursor-pointer hover:bg-gray-50' : 'cursor-default'}`
      : `relative flex overflow-hidden rounded-lg border border-gray-800 border-l-4 ${borderColor} bg-gray-950 shadow-2xl transition-colors ${clickable ? 'cursor-pointer hover:bg-gray-900/80' : 'cursor-default'}`,
    style: { minHeight: '84px', width: '340px' },
    onClick: clickable ? handlePersonClick : undefined,
  }, [
    // Left photo strip
    React.createElement('div', {
      key: 'photo',
      className: isLight ? 'w-[72px] flex-shrink-0 relative bg-gray-200' : 'w-[72px] flex-shrink-0 relative bg-gray-900',
    },
      metadata?.facePhotoUrl
        ? [
            React.createElement('img', {
              key: 'img',
              src: metadata.facePhotoUrl, alt: metadata?.personName || '',
              className: 'absolute inset-0 w-full h-full object-cover',
              onError: (e: any) => { e.currentTarget.style.display = 'none'; },
            }),
            isRFIDPersonAccess && React.createElement('div', {
              key: 'tagbadge',
              className: `absolute bottom-1 right-1 rounded-full p-1 ring-2 flex items-center justify-center ${isLight ? 'ring-white' : 'ring-gray-950'} ${type === 'success' ? 'bg-green-500' : 'bg-orange-500'}`,
              title: 'Acesso por tag',
            }, React.createElement(Wifi, { className: 'h-2.5 w-2.5 text-white' })),
          ]
        : React.createElement('div', {
            className: isUnknownTag
              ? (isLight ? 'absolute inset-0 w-full h-full flex items-center justify-center bg-violet-50' : 'absolute inset-0 w-full h-full flex items-center justify-center bg-violet-500/10')
              : isRFIDPersonAccess
              ? (isLight
                  ? `absolute inset-0 w-full h-full flex items-center justify-center ${type === 'success' ? 'bg-green-50' : 'bg-orange-50'}`
                  : `absolute inset-0 w-full h-full flex items-center justify-center ${type === 'success' ? 'bg-green-500/15' : 'bg-orange-500/15'}`)
              : metadata?.isRemoteTrigger
              ? (isLight ? 'absolute inset-0 w-full h-full flex items-center justify-center bg-indigo-100' : 'absolute inset-0 w-full h-full flex items-center justify-center bg-indigo-500/15')
              : (isLight
                  ? `absolute inset-0 w-full h-full flex items-center justify-center ${type === 'success' ? 'bg-green-100' : type === 'warning' ? 'bg-orange-100' : type === 'info' ? 'bg-blue-100' : 'bg-red-100'}`
                  : `absolute inset-0 w-full h-full flex items-center justify-center ${type === 'success' ? 'bg-green-500/10' : type === 'warning' ? 'bg-orange-500/10' : type === 'info' ? 'bg-blue-500/10' : 'bg-red-500/10'}`),
          },
            isUnknownTag ? React.createElement(Wifi, { className: isLight ? 'h-8 w-8 text-violet-500' : 'h-8 w-8 text-violet-400' })
            : isRFIDPersonAccess ? React.createElement(Wifi, { className: isLight ? `h-8 w-8 ${type === 'success' ? 'text-green-600' : 'text-orange-600'}` : `h-8 w-8 ${type === 'success' ? 'text-green-400' : 'text-orange-400'}` })
            : metadata?.isRemoteTrigger ? React.createElement(Fingerprint, { className: isLight ? 'h-8 w-8 text-indigo-600' : 'h-8 w-8 text-indigo-400' })
            : React.createElement(Fingerprint, { className: isLight ? 'h-8 w-8 text-gray-500' : 'h-8 w-8 text-gray-400' })
          )
    ),

    // Right content
    React.createElement('div', { key: 'content', className: 'flex-1 min-w-0 p-2.5 pr-7' }, [
      // Name row
      isUnknownTag
        ? React.createElement('div', { key: 'name', className: 'flex items-center gap-1 flex-nowrap min-w-0 overflow-hidden' }, [
            React.createElement('span', {
              key: 'tnum',
              className: 'inline-flex items-center px-1.5 py-0.5 rounded border font-mono text-[11px] font-bold bg-gray-900 border-gray-600 text-gray-100 tracking-wider max-w-[130px]',
              style: { overflow: 'hidden', flexShrink: 1, minWidth: 0, textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
            }, metadata?.personName),
            React.createElement('span', { key: 'tlabel', className: 'text-[10px] font-medium text-gray-500 whitespace-nowrap flex-shrink-0' }, 'Tag não cadastrada'),
          ])
        : metadata?.isRemoteTrigger
          ? React.createElement('div', { key: 'name' }, [
              React.createElement('p', { key: 'rl', className: `text-[10px] font-semibold ${isLight ? 'text-indigo-700' : 'text-indigo-400'} tracking-wide uppercase` }, 'Abertura Remota'),
              metadata?.triggeredBy && React.createElement('p', { key: 'rb', className: `text-xs font-medium ${isLight ? 'text-gray-800' : 'text-gray-100'} truncate` }, metadata.triggeredBy),
            ])
          : React.createElement('p', {
              key: 'name',
              className: isLight ? 'font-semibold text-gray-900 text-sm leading-snug truncate' : 'font-semibold text-gray-100 text-sm leading-snug truncate',
            }, metadata?.personName || message),

      // Não registrado badge
      !clickable && !metadata?.isRemoteTrigger && React.createElement('span', {
        key: 'unreg',
        className: isLight ? 'inline-flex items-center px-1.5 py-0.5 mt-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-700' : 'inline-flex items-center px-1.5 py-0.5 mt-0.5 rounded text-[10px] font-medium bg-orange-500/20 text-orange-400',
      }, 'Não registrado'),

      // Role + unit row
      !metadata?.isRemoteTrigger && metaRow,

      // Direction + zone row
      React.createElement('div', { key: 'dir-row', className: 'flex items-center gap-1.5 mt-1 flex-wrap' }, [dirBadge, zoneEl]),
    ]),

    closeSvg,
  ]);
};

// This will be populated by the provider
let getPreferences: (() => any) | null = null;
let getShouldShow: ((eventType?: NotificationEventType) => boolean) | null = null;

/**
 * Initialize the smart notification system with preferences
 * This should be called from the NotificationPreferences provider
 */
export const initializeSmartNotifications = (
  preferencesGetter: () => any,
  shouldShowGetter: (eventType?: NotificationEventType) => boolean
) => {
  getPreferences = preferencesGetter;
  getShouldShow = shouldShowGetter;
};

/**
 * Why an access-event toast did not appear.
 *
 * Every gate here is silent by design — preferences off, a Do-Not-Disturb window, the
 * per-event switch, `showToasts` — so "no toasts anymore" looks identical to a broken
 * pipeline from the outside, and the only way to tell them apart was to read this file.
 * One line in the console names the gate that stopped it.
 */
const logToastSuppressed = (eventType: NotificationEventType | undefined, prefs: any) => {
  console.info('[SmartNotification] toast suppressed by preferences', {
    eventType,
    enabled: prefs?.enabled,
    showToasts: prefs?.showToasts,
    dndEnabled: prefs?.dndEnabled ?? prefs?.dnd?.enabled,
    events: prefs?.events,
  });
};

interface NotifyOptions {
  duration?: number;
  icon?: string;
  eventType?: NotificationEventType;
  requireInteraction?: boolean;
  silent?: boolean;
  onClick?: () => void;
  metadata?: Record<string, any>;
}

/**
 * Show success notification
 */
const success = (message: string, description?: string, options: NotifyOptions = {}) => {
  // Always add to notification center first with metadata
  addToNotificationCenter(message, description || '', 'success', options.metadata);

  if (!getShouldShow || !getPreferences) {
    // Fallback if not initialized - still show toast
    toast.success(description || message);
    return;
  }

  // Check if should show this notification
  if (!getShouldShow(options.eventType)) {
    return;
  }

  const prefs = getPreferences();

  // Toast notification
  if (prefs.showToasts) {
    toast.success(description || message, {
      duration: options.duration || prefs.toastDuration,
      position: prefs.toastPosition,
    });
  }

  // Browser notification (if page is not focused)
  if (prefs.showBrowserNotifications && shouldShowBrowserNotification()) {
    showBrowserNotification(message, {
      body: description,
      icon: options.icon,
      tag: 'success',
      requireInteraction: options.requireInteraction,
      silent: options.silent || !prefs.playSound,
      onClick: options.onClick,
    });
  }

  // Sound
  if (prefs.playSound && !options.silent) {
    playNotificationSound();
  }
};

/**
 * Show error notification
 */
const error = (message: string, description?: string, options: NotifyOptions = {}) => {
  // Always add to notification center first with metadata
  addToNotificationCenter(message, description || '', 'error', options.metadata);

  if (!getShouldShow || !getPreferences) {
    // Fallback if not initialized - still show toast
    toast.error(description || message);
    return;
  }

  // Check if should show this notification
  if (!getShouldShow(options.eventType)) {
    return;
  }

  const prefs = getPreferences();

  // Toast notification
  if (prefs.showToasts) {
    toast.error(description || message, {
      duration: options.duration || prefs.toastDuration,
      position: prefs.toastPosition,
    });
  }

  // Browser notification (if page is not focused)
  if (prefs.showBrowserNotifications && shouldShowBrowserNotification()) {
    showBrowserNotification(message, {
      body: description,
      icon: options.icon,
      tag: 'error',
      requireInteraction: options.requireInteraction || true, // Errors should require interaction
      silent: options.silent || !prefs.playSound,
      onClick: options.onClick,
    });
  }

  // Sound
  if (prefs.playSound && !options.silent) {
    playNotificationSound();
  }
};

/**
 * Show warning notification
 */
const warning = (message: string, description?: string, options: NotifyOptions = {}) => {
  // Always add to notification center first with metadata
  addToNotificationCenter(message, description || '', 'warning', options.metadata);

  if (!getShouldShow || !getPreferences) {
    // Fallback if not initialized - still show toast
    toast(description || message);
    return;
  }

  // Check if should show this notification
  if (!getShouldShow(options.eventType)) {
    return;
  }

  const prefs = getPreferences();

  // Toast notification
  if (prefs.showToasts) {
    toast(description || message, {
      duration: options.duration || prefs.toastDuration,
      position: prefs.toastPosition,
    });
  }

  // Browser notification (if page is not focused)
  if (prefs.showBrowserNotifications && shouldShowBrowserNotification()) {
    showBrowserNotification(message, {
      body: description,
      icon: options.icon,
      tag: 'warning',
      requireInteraction: options.requireInteraction,
      silent: options.silent || !prefs.playSound,
      onClick: options.onClick,
    });
  }

  // Sound
  if (prefs.playSound && !options.silent) {
    playNotificationSound();
  }
};

/**
 * Show info notification
 */
const info = (message: string, description?: string, options: NotifyOptions = {}) => {
  // Always add to notification center first with metadata
  addToNotificationCenter(message, description || '', 'info', options.metadata);

  if (!getShouldShow || !getPreferences) {
    // Fallback if not initialized - still show toast
    toast(description || message);
    return;
  }

  // Check if should show this notification
  if (!getShouldShow(options.eventType)) {
    return;
  }

  const prefs = getPreferences();

  // Toast notification
  if (prefs.showToasts) {
    toast(description || message, {
      duration: options.duration || prefs.toastDuration,
      position: prefs.toastPosition,
    });
  }

  // Browser notification (if page is not focused)
  if (prefs.showBrowserNotifications && shouldShowBrowserNotification()) {
    showBrowserNotification(message, {
      body: description,
      icon: options.icon,
      tag: 'info',
      requireInteraction: options.requireInteraction,
      silent: options.silent || !prefs.playSound,
      onClick: options.onClick,
    });
  }

  // Sound (usually no sound for info)
  if (prefs.playSound && !options.silent && options.eventType) {
    playNotificationSound();
  }
};

const ROLE_LABELS: Record<string, string> = {
  // person_type values
  'resident':        'Morador',
  'morador':         'Morador',
  'proprietario':    'Proprietário',
  'owner':           'Proprietário',
  'visitor':         'Visitante',
  'visitante':       'Visitante',
  'staff':           'Funcionário',
  'funcionario':     'Funcionário',
  'employee':        'Funcionário',
  'service_provider':'Prestador',
  'prestador':       'Prestador',
  'delivery':        'Entregador',
  'entregador':      'Entregador',
  'security':        'Segurança',
  'guard':           'Segurança',
  // person_role values
  'admin':           'Administrador',
  'manager':         'Gerente',
  'concierge':       'Porteiro',
  'porteiro':        'Porteiro',
};

const translateRole = (value?: string): string | undefined => {
  if (!value) return undefined;
  return ROLE_LABELS[value.toLowerCase()] ?? value;
};

/**
 * Show access event notification
 * Specialized notification for access control events
 */
interface AccessEventData {
  personId?: string;
  personName?: string;
  deviceName?: string;
  zoneName?: string;
  direction?: string; // 'entrada', 'saida', 'passagem'
  facePhotoUrl?: string;
  eventType: 'authentication_success' | 'unauthorized' | 'door_opened' | 'door_closed' | 'remote_door_trigger';
  timestamp: Date;
  // Enriched fields
  customerName?: string;
  customerType?: string;
  unitName?: string;
  streetName?: string;
  departmentName?: string;
  personType?: string;
  personRole?: string;
  // RFID-specific fields
  vehicleId?: string;
  vehiclePlate?: string;
  accessType?: 'rfid' | string; // 'rfid' for vehicle access
  // Manual operation flag
  isManual?: boolean;
}

const accessEvent = (data: AccessEventData, options: NotifyOptions = {}) => {
  console.log(' [SmartNotification] accessEvent called with:', data);
  const {
    personId,
    personName,
    deviceName,
    zoneName,
    direction,
    facePhotoUrl,
    eventType,
    timestamp,
    customerName,
    customerType,
    unitName,
    streetName,
    departmentName,
    personType,
    personRole,
    vehicleId,
    vehiclePlate,
    accessType,
    isManual,
  } = data;

  // Detect if this is an RFID/vehicle event
  const isRFIDVehicleEvent = !!vehiclePlate;

  // Map event type to notification event type
  let notificationEventType: NotificationEventType;
  let title: string;
  let message: string;

  // Create metadata with all enriched data
  const metadata: Record<string, any> = {};
  if (personId) metadata.personId = personId;
  if (facePhotoUrl) metadata.facePhotoUrl = facePhotoUrl;
  if (direction) metadata.direction = direction;
  if (zoneName) metadata.zoneName = zoneName;
  if (!zoneName && deviceName) metadata.deviceNameFallback = deviceName;
  if (personName) metadata.personName = personName;
  if (customerName) metadata.customerName = customerName;
  if (customerType) metadata.customerType = customerType;
  if (unitName) metadata.unitName = unitName;
  if (streetName) metadata.streetName = streetName;
  if (departmentName) metadata.departmentName = departmentName;
  if (personType) metadata.personType = translateRole(personType) ?? personType;
  if (personRole) metadata.personRole = translateRole(personRole) ?? personRole;
  if (isManual) metadata.isManual = true;
  // RFID-specific metadata
  if (vehicleId) metadata.vehicleId = vehicleId;
  if (vehiclePlate) metadata.vehiclePlate = vehiclePlate;
  if (accessType) metadata.accessType = accessType;

  // Build enriched description for browser notifications
  const buildEnrichedDescription = (baseMessage: string): string => {
    const parts = [baseMessage];
    if (customerName) parts.push(`${customerName}`);
    if (unitName) parts.push(`${unitName}`);
    if (departmentName) parts.push(`${departmentName}`);
    if (personRole) parts.push(`${personRole}`);
    return parts.join(' • ');
  };

  switch (eventType) {
    case 'authentication_success':
      notificationEventType = 'access_granted';
      title = isRFIDVehicleEvent ? 'Acesso Veicular' : 'Acesso Autorizado';
      message = `${personName || 'Pessoa'}`;
      const enrichedSuccessMessage = buildEnrichedDescription(message);
      console.log(' [SmartNotification] Showing success notification:', { title, message, metadata });

      // Add to notification center with simple message (rich data is in metadata)
      addToNotificationCenter(title, message, 'success', metadata);

      // Check if should show - with fallback for when preferences not initialized
      if (!getShouldShow || !getPreferences) {
        // Fallback: show toast with defaults when preferences not initialized
        console.log(' [SmartNotification] Preferences not initialized, showing fallback toast');
        toast.custom(
          (t) => React.createElement(AccessEventToast, {
            title,
            message,
            metadata,
            type: 'success',
            toastId: t.id,
            clickable: !!(personId || vehiclePlate),
          }),
          {
            duration: options.duration || 5000,
            position: 'top-right',
          }
        );
      } else if (!getShouldShow(notificationEventType)) {
        logToastSuppressed(notificationEventType, getPreferences());
      } else {
        const prefs = getPreferences();

        // Show custom toast with photo and badges
        if (!prefs.showToasts) logToastSuppressed(notificationEventType, prefs);
        if (prefs.showToasts) {
          toast.custom(
            (t) => React.createElement(AccessEventToast, {
              title,
              message,
              metadata,
              type: 'success',
              toastId: t.id,
              clickable: !!(personId || vehiclePlate),
            }),
            {
              duration: options.duration || prefs.toastDuration,
              position: prefs.toastPosition,
            }
          );
        }

        // Browser notification (use enriched message for OS notification)
        if (prefs.showBrowserNotifications && shouldShowBrowserNotification()) {
          showBrowserNotification(title, {
            body: enrichedSuccessMessage,
            icon: options.icon,
            tag: 'success',
            requireInteraction: options.requireInteraction,
            silent: options.silent || !prefs.playSound,
            onClick: options.onClick,
          });
        }

        // Sound
        if (prefs.playSound && !options.silent) {
          playNotificationSound();
        }
      }
      break;

    case 'unauthorized':
      // Show toast for unregistered/unauthorized people if they have a name or plate (LPR)
      if (!personName && !vehiclePlate) break;

      notificationEventType = 'access_granted';
      title = vehiclePlate && !personName ? 'Placa Não Cadastrada' : 'Acesso Negado';
      message = personName || vehiclePlate || '';

      addToNotificationCenter(title, message, 'warning', metadata);

      {
        const showToast = (t: any) => React.createElement(AccessEventToast, {
          title,
          message,
          metadata,
          type: 'warning',
          toastId: t.id,
          clickable: false,   // no personId → not clickable
        });

        if (!getShouldShow || !getPreferences) {
          toast.custom(showToast, { duration: options.duration || 5000, position: 'top-right' });
        } else if (!getShouldShow(notificationEventType)) {
          logToastSuppressed(notificationEventType, getPreferences());
        } else {
          const prefs = getPreferences();
          if (!prefs.showToasts) logToastSuppressed(notificationEventType, prefs);
          if (prefs.showToasts) {
            toast.custom(showToast, { duration: options.duration || prefs.toastDuration, position: prefs.toastPosition });
          }
          if (prefs.playSound && !options.silent) playNotificationSound();
        }
      }
      break;

    case 'door_opened':
    case 'door_closed':
      notificationEventType = 'door_events';
      title = eventType === 'door_opened' ? 'Porta Aberta' : 'Porta Fechada';
      message = `${zoneName || deviceName || 'Dispositivo'}`;
      console.log(' [SmartNotification] Showing info notification:', { title, message, metadata });
      // info() will handle both toast AND notification center
      info(title, message, { ...options, eventType: notificationEventType });
      break;

    case 'remote_door_trigger':
      notificationEventType = 'access_granted';
      title = 'Abertura Remota';
      message = 'Abertura Remota';
      metadata.isRemoteTrigger = true;
      metadata.triggeredBy = personName || undefined; // operator name shown as subtitle
      metadata.personName = 'Abertura Remota'; // override so toast header shows label, not operator name
      if (!metadata.zoneName && deviceName) metadata.deviceNameFallback = deviceName; // zone fallback

      addToNotificationCenter(title, message, 'info', metadata);

      {
        const showRemoteToast = (t: any) => React.createElement(AccessEventToast, {
          title,
          message,
          metadata,
          type: 'info',
          toastId: t.id,
          clickable: false,
        });

        if (!getShouldShow || !getPreferences) {
          toast.custom(showRemoteToast, { duration: options.duration || 5000, position: 'top-right' });
        } else if (!getShouldShow(notificationEventType)) {
          logToastSuppressed(notificationEventType, getPreferences());
        } else {
          const prefs = getPreferences();
          if (!prefs.showToasts) logToastSuppressed(notificationEventType, prefs);
          if (prefs.showToasts) {
            toast.custom(showRemoteToast, { duration: options.duration || prefs.toastDuration, position: prefs.toastPosition });
          }
          if (prefs.playSound && !options.silent) playNotificationSound();
        }
      }
      break;
  }
  console.log(' [SmartNotification] accessEvent completed');
};

/**
 * Show device alert notification
 */
interface DeviceAlertData {
  deviceName: string;
  alertType: 'low_battery' | 'offline' | 'error';
  message: string;
}

const deviceAlert = (data: DeviceAlertData, options: NotifyOptions = {}) => {
  const { deviceName, alertType, message: alertMessage } = data;

  let title: string;
  switch (alertType) {
    case 'low_battery':
      title = 'Bateria Baixa';
      break;
    case 'offline':
      title = 'Dispositivo Offline';
      break;
    case 'error':
      title = 'Erro no Dispositivo';
      break;
  }

  warning(title, `${deviceName}: ${alertMessage}`, {
    ...options,
    eventType: 'device_alerts',
  });
};

/**
 * Show system alert notification
 */
const systemAlert = (title: string, message: string, options: NotifyOptions = {}) => {
  info(title, message, { ...options, eventType: 'system_alerts' });
};

/**
 * Show a simple action-confirmation toast card.
 * Appears in the react-hot-toast queue alongside SSE event toasts.
 * Does NOT duplicate event details — just confirms the operator action.
 */
const ConfirmToast = ({
  message,
  type,
  toastId,
}: {
  message: string;
  type: 'success' | 'info' | 'error';
  toastId: string;
}) => {
  const isLight = typeof document !== 'undefined' && document.body.classList.contains('panther-light-toast');

  const borderColor = type === 'success' ? 'border-l-green-500' : type === 'info' ? 'border-l-blue-500' : 'border-l-red-500';
  const iconBg = isLight
    ? type === 'success' ? 'bg-green-100' : type === 'info' ? 'bg-blue-100' : 'bg-red-100'
    : type === 'success' ? 'bg-green-500/10' : type === 'info' ? 'bg-blue-500/10' : 'bg-red-500/10';
  const iconColor = isLight
    ? type === 'success' ? 'text-green-600' : type === 'info' ? 'text-blue-600' : 'text-red-600'
    : type === 'success' ? 'text-green-400' : type === 'info' ? 'text-blue-400' : 'text-red-400';

  const handleClose = (e: any) => {
    e.stopPropagation();
    toast.dismiss(toastId);
  };

  return React.createElement('div', {
    className: isLight
      ? `relative flex items-center overflow-hidden rounded-lg border border-gray-300 border-l-4 ${borderColor} bg-white shadow-lg`
      : `relative flex items-center overflow-hidden rounded-lg border border-gray-800 border-l-4 ${borderColor} bg-gray-900 shadow-2xl`,
    style: { minHeight: '56px', width: '340px' },
  }, [
    React.createElement('div', {
      key: 'icon',
      className: `w-14 flex-shrink-0 flex items-center justify-center self-stretch ${iconBg}`,
    },
      React.createElement('svg', {
        className: `w-6 h-6 ${iconColor}`,
        fill: 'none', viewBox: '0 0 24 24', stroke: 'currentColor',
      }, React.createElement('path', {
        strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 2,
        d: type === 'success' || type === 'info'
          ? 'M5 13l4 4L19 7'
          : 'M6 18L18 6M6 6l12 12',
      }))
    ),
    React.createElement('p', {
      key: 'msg',
      className: isLight
        ? 'flex-1 px-3 py-3 text-sm font-semibold text-gray-900'
        : 'flex-1 px-3 py-3 text-sm font-semibold text-gray-100',
    }, message),
    React.createElement('button', {
      key: 'close',
      onClick: handleClose,
      className: isLight
        ? 'absolute top-1.5 right-1.5 p-1 hover:bg-black/10 rounded-full transition-colors'
        : 'absolute top-1.5 right-1.5 p-1 hover:bg-white/10 rounded-full transition-colors',
    }, React.createElement('svg', {
      className: isLight ? 'w-3 h-3 text-gray-600' : 'w-3 h-3 text-gray-500',
      fill: 'none', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '2.5',
      viewBox: '0 0 24 24', stroke: 'currentColor',
    }, React.createElement('path', { d: 'M6 18L18 6M6 6l12 12' }))),
  ]);
};

const confirm = (message: string, type: 'success' | 'info' | 'error' = 'success') => {
  toast.custom(
    (t) => React.createElement(ConfirmToast, { message, type, toastId: t.id }),
    { duration: 4000, position: 'top-right' }
  );
};

// Export the notify API
export const notify = {
  success,
  error,
  warning,
  info,
  accessEvent,
  deviceAlert,
  systemAlert,
  confirm,
};
